"use client";

import { Capacitor } from "@capacitor/core";
import {
  ensureAppleMusicKitInstance,
  getCachedAppleMusicInstance,
} from "./appleMusicKit";
import {
  APPLE_MUSIC_TEMPORARY_CODE,
  getAppleMusicSessionErrorState,
} from "./appleMusicClientError.js";
import { normalizeReturnTo } from "./authNavigation";
import { appendAppleMusicDebugEntry } from "./appleMusicDebug";
import { AppleMusicNativeAuth } from "./appleMusicNativeAuth";
import { reportProviderClientIssue } from "./providerClientIssue";
import { isNativeIOS, isNativePlatform } from "./runtime";

type AppleMusicSessionResponse = {
  ok?: boolean;
  linked?: boolean;
  returnTo?: string;
  user?: {
    user_id?: string | null;
    provider?: string | null;
  };
  error?: string;
  code?: string | null;
  correlationId?: string | null;
  retryable?: boolean;
};

type AppleMusicNativeLoginConfig = {
  developerToken: string;
  loginNonce: string;
  storefront?: string | null;
};

export type AppleMusicAuthorizeResult = {
  musicUserToken: string;
  storefront?: string | null;
  loginNonce?: string | null;
  canPlayCatalogContent?: boolean | null;
  tokenIssuedAt?: number | null;
};

export type AppleMusicClientDebug = {
  appEnv: string | null;
  flowKind: "native" | "web";
  nativePlatform: boolean;
  nativeIOS: boolean;
  capacitorPlatform: string | null;
  nativePluginAvailable: boolean | null;
  hasMusicUserToken: boolean;
  hasLoginNonce: boolean;
  hasTokenIssuedAt: boolean;
  canPlayCatalogContent: boolean | null;
};

export type AppleMusicErrorTone = "error" | "warn" | "info";

const APPLE_AUTHORIZE_TIMEOUT_MS = 75_000;
const APPLE_SESSION_TIMEOUT_MS = 20_000;

export class AppleMusicSessionError extends Error {
  code: string | null;
  correlationId: string | null;
  retryable: boolean;
  tone: AppleMusicErrorTone;

  constructor(
    message: string,
    {
      code = null,
      correlationId = null,
      retryable = false,
      tone = "error",
    }: {
      code?: string | null;
      correlationId?: string | null;
      retryable?: boolean;
      tone?: AppleMusicErrorTone;
    } = {}
  ) {
    super(message);
    this.name = "AppleMusicSessionError";
    this.code = code;
    this.correlationId = correlationId;
    this.retryable = retryable;
    this.tone = tone;
  }
}

function asRecord(value: unknown): Record<string, unknown> {
  if (!value || typeof value !== "object") return {};
  return value as Record<string, unknown>;
}

function sleep(ms: number) {
  return new Promise<void>((resolve) => {
    window.setTimeout(resolve, ms);
  });
}

function readMusicUserToken(music: {
  musicUserToken?: string | null;
} | null | undefined): string {
  if (!music) return "";
  return typeof music.musicUserToken === "string" ? music.musicUserToken.trim() : "";
}

function asErrorMessage(err: unknown, fallback: string): string {
  if (err instanceof Error && err.message.trim()) return err.message.trim();
  return fallback;
}

function firstNonEmptyString(...values: unknown[]) {
  for (const value of values) {
    if (typeof value === "string" && value.trim()) {
      return value.trim();
    }
  }
  return null;
}

function isAbortError(err: unknown): boolean {
  if (err instanceof DOMException && err.name === "AbortError") return true;
  const parsed = asRecord(err);
  return parsed.name === "AbortError";
}

function getCapacitorPlatform() {
  try {
    const platform = Capacitor.getPlatform();
    return typeof platform === "string" && platform.trim() ? platform.trim() : null;
  } catch {
    return null;
  }
}

function isAppleMusicNativePluginAvailable() {
  try {
    return Capacitor.isPluginAvailable("AppleMusicNativeAuth");
  } catch {
    return null;
  }
}

export function buildAppleMusicClientDebug(
  authorizeResult: AppleMusicAuthorizeResult,
  musicUserToken: string,
  nativeLogin: boolean
): AppleMusicClientDebug {
  const nativePlatform = isNativePlatform();

  return {
    appEnv: firstNonEmptyString(process.env.NEXT_PUBLIC_APP_ENV),
    flowKind: nativeLogin ? "native" : "web",
    nativePlatform,
    nativeIOS: nativeLogin,
    capacitorPlatform: getCapacitorPlatform(),
    nativePluginAvailable: nativePlatform
      ? isAppleMusicNativePluginAvailable()
      : null,
    hasMusicUserToken: Boolean(musicUserToken),
    hasLoginNonce: Boolean(firstNonEmptyString(authorizeResult.loginNonce)),
    hasTokenIssuedAt:
      typeof authorizeResult.tokenIssuedAt === "number" &&
      Number.isFinite(authorizeResult.tokenIssuedAt),
    canPlayCatalogContent:
      typeof authorizeResult.canPlayCatalogContent === "boolean"
        ? authorizeResult.canPlayCatalogContent
        : null,
  };
}

function createAppleMusicSessionError(
  payload: Record<string, unknown>,
  status: number
) {
  const resolved = getAppleMusicSessionErrorState(payload, status);

  return new AppleMusicSessionError(resolved.message, {
    code: resolved.code,
    correlationId: resolved.correlationId,
    retryable: resolved.retryable,
    tone: resolved.tone as AppleMusicErrorTone,
  });
}

function createLocalAppleMusicSessionError(
  message: string,
  {
    code = null,
    retryable = false,
    tone = "error",
  }: {
    code?: string | null;
    retryable?: boolean;
    tone?: AppleMusicErrorTone;
  } = {}
) {
  return new AppleMusicSessionError(message, {
    code,
    retryable,
    tone,
  });
}

function describeAppleMusicDebugError(error: unknown) {
  const parsed = asRecord(error);
  const errorData = asRecord(parsed.data);

  return {
    message: asErrorMessage(error, "Unknown Apple Music error"),
    code: firstNonEmptyString(parsed.code, errorData.code),
    authorizationStatus: firstNonEmptyString(errorData.authorizationStatus),
  };
}

function logAppleMusicAuthDebug(
  event: string,
  {
    level = "info",
    persist = false,
    details = {},
    error,
  }: {
    level?: AppleMusicErrorTone;
    persist?: boolean;
    details?: Record<string, unknown>;
    error?: unknown;
  } = {}
) {
  const entryDetails = {
    ...details,
    ...(error !== undefined ? { error: describeAppleMusicDebugError(error) } : {}),
  };

  if (level === "error") {
    console.error(`[AppleMusicAuth] ${event}`, entryDetails);
  } else if (level === "warn") {
    console.warn(`[AppleMusicAuth] ${event}`, entryDetails);
  } else {
    console.info(`[AppleMusicAuth] ${event}`, entryDetails);
  }

  appendAppleMusicDebugEntry({
    event,
    level,
    persist,
    details: entryDetails,
  });

  if (level === "warn" || level === "error") {
    reportProviderClientIssue({
      provider: "applemusic",
      category: "auth",
      level,
      event,
      title: "Apple Music sign-in or session issue",
      message:
        error instanceof Error && error.message
          ? error.message
          : persist
            ? event
            : null,
      error,
      details: entryDetails,
    });
  }
}

export function resolveAppleMusicErrorState(err: unknown) {
  if (err instanceof AppleMusicSessionError) {
    return {
      message: err.message,
      tone: err.tone,
      code: err.code,
      correlationId: err.correlationId,
      retryable: err.retryable,
    };
  }

  return {
    message: asErrorMessage(err, "Apple Music sign-in failed."),
    tone: "error" as const,
    code: null,
    correlationId: null,
    retryable: false,
  };
}

async function withTimeout<T>(
  promise: Promise<T>,
  timeoutMs: number,
  timeoutMessage: string
): Promise<T> {
  let timerId: number | null = null;
  try {
    return await new Promise<T>((resolve, reject) => {
      timerId = window.setTimeout(() => {
        reject(new Error(timeoutMessage));
      }, timeoutMs);
      promise.then(resolve, reject);
    });
  } finally {
    if (timerId != null) window.clearTimeout(timerId);
  }
}

async function waitForMusicUserToken(
  music: { musicUserToken?: string | null },
  timeoutMs: number
) {
  const started = Date.now();
  while (Date.now() - started < timeoutMs) {
    const token = readMusicUserToken(music);
    if (token) return token;
    await sleep(100);
  }
  return readMusicUserToken(music);
}

async function resolveMusicUserToken({
  authorizePromise,
  music,
  timeoutMs,
}: {
  authorizePromise: Promise<string>;
  music: { musicUserToken?: string | null };
  timeoutMs: number;
}) {
  const pendingErrors: Error[] = [];

  const tokenFromAuthorizePromise = authorizePromise.then((token) => {
    const normalizedToken =
      typeof token === "string" ? token.trim() : "";
    if (!normalizedToken) {
      throw new Error("Apple Music authorization did not return a user token");
    }
    return normalizedToken;
  });

  const tokenFromStatePromise = waitForMusicUserToken(music, timeoutMs).then(
    (token) => {
      const normalizedToken = token.trim();
      if (!normalizedToken) {
        throw new Error("Apple Music authorization did not return a user token");
      }
      return normalizedToken;
    }
  );

  return withTimeout(
    new Promise<string>((resolve, reject) => {
      let settled = false;
      let rejectionCount = 0;

      const finishResolve = (token: string) => {
        if (settled) return;
        settled = true;
        resolve(token);
      };

      const finishReject = (err: unknown) => {
        if (settled) return;
        rejectionCount += 1;
        pendingErrors.push(
          err instanceof Error
            ? err
            : new Error(asErrorMessage(err, "Apple Music authorization failed"))
        );
        if (rejectionCount >= 2) {
          settled = true;
          reject(pendingErrors[pendingErrors.length - 1]);
        }
      };

      tokenFromAuthorizePromise.then(finishResolve, finishReject);
      tokenFromStatePromise.then(finishResolve, finishReject);
    }),
    timeoutMs,
    "Apple Music authorization timed out. Please try again."
  );
}

function nativeAppleMusicErrorMessage(err: unknown) {
  const parsed = asRecord(err);
  const errorCode =
    typeof parsed.code === "string" ? parsed.code.trim().toUpperCase() : "";
  const errorData = asRecord(parsed.data);
  const authorizationStatus =
    typeof errorData.authorizationStatus === "string"
      ? errorData.authorizationStatus.trim().toLowerCase()
      : "";
  const message = asErrorMessage(err, "Native Apple Music authorization failed.");

  if (
    errorCode === "UNIMPLEMENTED" ||
    errorCode === "UNAVAILABLE" ||
    message.toLowerCase().includes("not implemented")
  ) {
    return "This iOS build does not include native Apple Music sign-in yet. Rebuild the app and try again.";
  }

  if (authorizationStatus === "denied" || authorizationStatus === "restricted") {
    return "Apple Music access is disabled for IMAGS on this iPhone. Enable Media & Apple Music access in Settings and try again.";
  }

  return message;
}

export async function fetchAppleMusicNativeLoginConfig(): Promise<AppleMusicNativeLoginConfig> {
  const res = await fetch("/api/apple/native/config", {
    credentials: "include",
  });

  const text = await res.text().catch(() => "");
  let payload: unknown = null;
  try {
    payload = text ? JSON.parse(text) : null;
  } catch {
    payload = null;
  }

  const parsed = asRecord(payload);
  if (!res.ok) {
    throw new Error(
      typeof parsed.error === "string" && parsed.error.trim()
        ? parsed.error.trim()
        : `Failed to load Apple Music native config (${res.status})`
    );
  }

  const developerToken = firstNonEmptyString(parsed.developerToken);
  const loginNonce = firstNonEmptyString(parsed.loginNonce);
  if (!developerToken || !loginNonce) {
    throw new Error("Apple Music native login config is incomplete");
  }

  return {
    developerToken,
    loginNonce,
    storefront: firstNonEmptyString(parsed.storefront),
  };
}

export async function authorizeAppleMusicOnNativeIOS(): Promise<AppleMusicAuthorizeResult> {
  logAppleMusicAuthDebug("nativeAuthorize:start", {
    details: {
      nativePlatform: isNativePlatform(),
      nativeIOS: isNativeIOS(),
      capacitorPlatform: getCapacitorPlatform(),
      nativePluginAvailable: isAppleMusicNativePluginAvailable(),
    },
  });
  const config = await fetchAppleMusicNativeLoginConfig();

  try {
    const nativePayload = await AppleMusicNativeAuth.authorize({
      developerToken: config.developerToken,
      loginNonce: config.loginNonce,
      // Native validation reuses the exact login developer token, so bypass
      // MusicKit's cached user token to avoid token/developer-token skew.
      ignoreCache: true,
    });

    const result = {
      musicUserToken: firstNonEmptyString(nativePayload?.musicUserToken) || "",
      storefront: firstNonEmptyString(nativePayload?.storefront, config.storefront),
      loginNonce: firstNonEmptyString(nativePayload?.loginNonce, config.loginNonce),
      canPlayCatalogContent:
        typeof nativePayload?.canPlayCatalogContent === "boolean"
          ? nativePayload.canPlayCatalogContent
          : null,
      tokenIssuedAt:
        typeof nativePayload?.tokenIssuedAt === "number" &&
        Number.isFinite(nativePayload.tokenIssuedAt)
          ? Math.floor(nativePayload.tokenIssuedAt)
          : null,
    };
    logAppleMusicAuthDebug("nativeAuthorize:completed", {
      details: {
        storefront: result.storefront ?? null,
        hasMusicUserToken: Boolean(result.musicUserToken),
        hasLoginNonce: Boolean(result.loginNonce),
        canPlayCatalogContent: result.canPlayCatalogContent,
        tokenIssuedAt: result.tokenIssuedAt,
      },
    });
    return result;
  } catch (err) {
    const parsed = asRecord(err);
    const errorData = asRecord(parsed.data);
    logAppleMusicAuthDebug("nativeAuthorize:failed", {
      level: "warn",
      persist: true,
      error: err,
      details: {
        code: firstNonEmptyString(parsed.code),
        authorizationStatus: firstNonEmptyString(errorData.authorizationStatus),
        capacitorPlatform: getCapacitorPlatform(),
        nativePluginAvailable: isAppleMusicNativePluginAvailable(),
      },
    });
    throw new Error(nativeAppleMusicErrorMessage(err));
  }
}

async function authorizeAppleMusicOnWeb(): Promise<AppleMusicAuthorizeResult> {
  let music = getCachedAppleMusicInstance();
  if (!music || typeof music.authorize !== "function") {
    console.warn("[AppleMusicAuth] MusicKit not ready at click time");
    try {
      const ensured = await withTimeout(
        ensureAppleMusicKitInstance(),
        20_000,
        "Apple Music initialization timed out. Please verify config and retry."
      );
      music = ensured?.music ?? null;
    } catch (err) {
      throw new Error(
        asErrorMessage(
          err,
          "Apple Music sign-in is unavailable. Check Apple Music configuration."
        )
      );
    }
  }

  if (!music || typeof music.authorize !== "function") {
    logAppleMusicAuthDebug("webAuthorize:authorize_unavailable", {
      level: "error",
      persist: true,
      details: {
        stage: "authorize_unavailable",
      },
    });
    throw new Error("Apple Music authorization is unavailable");
  }

  let authorizePromise: Promise<string>;
  // Safari requires this to originate from a direct user gesture.
  try {
    authorizePromise = music.authorize();
  } catch (error) {
    logAppleMusicAuthDebug("webAuthorize:authorize_unavailable", {
      level: "error",
      persist: true,
      error,
      details: {
        stage: "authorize_call_failed",
      },
    });
    throw new Error("Apple Music authorization is unavailable");
  }

  let musicUserToken = readMusicUserToken(music);
  if (!musicUserToken) {
    try {
      musicUserToken = await resolveMusicUserToken({
        authorizePromise,
        music,
        timeoutMs: APPLE_AUTHORIZE_TIMEOUT_MS,
      });
    } catch (err) {
      console.warn("[AppleMusicAuth] authorize() failed or timed out", {
        error: asErrorMessage(err, "unknown"),
      });
      const recoveredToken = await waitForMusicUserToken(music, 3000);
      if (recoveredToken) {
        musicUserToken = recoveredToken;
      } else {
        logAppleMusicAuthDebug("webAuthorize:token_resolution_failed", {
          level: "error",
          persist: true,
          error: err,
          details: {
            stage: "resolve_music_user_token",
          },
        });
        throw new Error(
          asErrorMessage(err, "Apple Music authorization failed. Please try again.")
        );
      }
    }
  }

  if (!musicUserToken || typeof musicUserToken !== "string" || !musicUserToken.trim()) {
    logAppleMusicAuthDebug("webAuthorize:missing_user_token", {
      level: "error",
      persist: true,
      details: {
        stage: "missing_user_token",
      },
    });
    throw new Error("Apple Music authorization did not return a user token");
  }

  return {
    musicUserToken: musicUserToken.trim(),
    storefront: firstNonEmptyString(music.storefrontId),
    loginNonce: null,
    canPlayCatalogContent: null,
    tokenIssuedAt: null,
  };
}

export async function signInWithAppleMusic(
  {
    returnTo = "/presurvey",
    linkAccountId = null,
    appleLinkToken = null,
  }: {
    returnTo?: string;
    linkAccountId?: string | null;
    appleLinkToken?: string | null;
  } = {}
) {
  const safeReturnTo = normalizeReturnTo(returnTo, "/presurvey");
  const nativeLogin = isNativeIOS();
  const normalizedLinkAccountId =
    typeof linkAccountId === "string" && linkAccountId.trim()
      ? linkAccountId.trim()
      : null;
  const normalizedAppleLinkToken =
    typeof appleLinkToken === "string" && appleLinkToken.trim()
      ? appleLinkToken.trim()
      : null;
  const authorizeResult = nativeLogin
    ? await authorizeAppleMusicOnNativeIOS()
    : await authorizeAppleMusicOnWeb();
  const normalizedMusicUserToken = authorizeResult.musicUserToken.trim();
  const clientDebug = buildAppleMusicClientDebug(
    authorizeResult,
    normalizedMusicUserToken,
    nativeLogin
  );

  const controller = new AbortController();
  const timerId = window.setTimeout(() => {
    controller.abort();
  }, APPLE_SESSION_TIMEOUT_MS);

  let res: Response;
  try {
    logAppleMusicAuthDebug("sessionVerify:start", {
      details: {
        ...clientDebug,
        storefront: authorizeResult.storefront ?? null,
        hasLinkAccountId: Boolean(normalizedLinkAccountId),
        hasAppleLinkToken: Boolean(normalizedAppleLinkToken),
      },
    });
    res = await fetch("/api/apple/session", {
      method: "POST",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
      },
      signal: controller.signal,
      body: JSON.stringify({
        musicUserToken: normalizedMusicUserToken,
        storefront: authorizeResult.storefront,
        returnTo: safeReturnTo,
        linkAccountId: normalizedLinkAccountId,
        appleLinkToken: normalizedAppleLinkToken,
        clientDebug,
        ...(nativeLogin
          ? {
              loginNonce: authorizeResult.loginNonce,
              canPlayCatalogContent: authorizeResult.canPlayCatalogContent,
              tokenIssuedAt: authorizeResult.tokenIssuedAt,
            }
          : {}),
      }),
    });
  } catch (err) {
    if (isAbortError(err)) {
      logAppleMusicAuthDebug("sessionVerify:timeout", {
        level: "warn",
        persist: true,
        details: clientDebug,
      });
      throw createLocalAppleMusicSessionError(
        "Apple Music sign-in completed, but verification is temporarily unavailable. Please try again.",
        {
          code: APPLE_MUSIC_TEMPORARY_CODE,
          retryable: true,
          tone: "info",
        }
      );
    }
    logAppleMusicAuthDebug("sessionVerify:request_failed", {
      level: "error",
      persist: true,
      error: err,
      details: clientDebug,
    });
    throw createLocalAppleMusicSessionError(
      asErrorMessage(
        err,
        "Apple Music sign-in completed, but we could not verify access. Please try again."
      ),
      {
        retryable: true,
        tone: "info",
      }
    );
  } finally {
    window.clearTimeout(timerId);
  }

  const text = await res.text().catch(() => "");
  let payload: AppleMusicSessionResponse | null = null;
  try {
    payload = text ? (JSON.parse(text) as AppleMusicSessionResponse) : null;
  } catch {
    payload = null;
  }

  if (!res.ok || payload?.ok !== true) {
    logAppleMusicAuthDebug("sessionVerify:rejected", {
      level: "warn",
      persist: true,
      details: {
        ...clientDebug,
        status: res.status,
        code: payload?.code ?? null,
        correlationId: payload?.correlationId ?? null,
      },
    });
    throw createAppleMusicSessionError(asRecord(payload), res.status);
  }

  logAppleMusicAuthDebug("sessionVerify:completed", {
    details: {
      ...clientDebug,
      linked: payload?.linked === true,
      returnTo:
        typeof payload?.returnTo === "string" ? payload.returnTo : safeReturnTo,
    },
  });

  return payload;
}
